import React, { useState, useRef, useEffect } from 'react';
import { LuFileText, LuVideo, LuBug } from 'react-icons/lu';
import { SLACK_ICON_URL, generateProfilePictureUrl } from 'src/constants/AppConstants';
import { HUDDLE_CONTACTS } from 'src/components/das-finsuite/ea-huddle-v2/constants/EAHuddleConstants';
import supportIcon from 'src/images/circle-question.svg';

// TODO: Make support links configurable per-app (each DasFinSuite app should define its own links).
// For now, these are EA Huddle+ specific.
const SUPPORT_LINKS = [
  {
    id: 'wiki',
    label: 'Huddle+ Wiki',
    description: 'Documentation & FAQs',
    href: 'https://w.amazon.com/bin/view/DaS-FinTech/EA-Huddle',
    icon: <LuFileText size={16} />,
    external: true
  },
  {
    id: 'demo',
    label: 'Watch Demo',
    description: 'Video walkthrough',
    href: 'https://broadcast.amazon.com/videos/1745869',
    icon: <LuVideo size={16} />,
    external: true
  },
  {
    id: 'quip',
    label: 'How to Use',
    description: 'Step-by-step guide',
    href: 'https://quip-amazon.com/XoV8A5qalhT9/EA-Huddle-How-to-Use',
    icon: <LuFileText size={16} />,
    external: true
  },
  {
    id: 'slack',
    label: 'Slack Community',
    description: '#huddle-plus-interest',
    href: 'https://amazon.enterprise.slack.com/archives/C0A38R2A42J',
    icon: <img src={SLACK_ICON_URL} alt="" width="16" height="16" />,
    external: true
  },
  {
    id: 'bug',
    label: 'Create Support Ticket',
    description: 'Report bugs or request help',
    href: 'https://t.corp.amazon.com/create/templates/8d963482-2c95-4b8f-bdbd-b9dbc1db0bc0',
    icon: <LuBug size={16} />,
    external: true
  }
];

const CONTACTS = HUDDLE_CONTACTS.slice(0, 2); // Show first 2 contacts in dropdown

export const SupportDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div className="support-dropdown" ref={dropdownRef}>
      <button
        className="dfp-top-nav__utility-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-label="Help & Support"
        title="Help & Support"
      >
        <img src={supportIcon} alt="Support" width="22" height="22" />
      </button>
      {isOpen && (
        <div className="support-dropdown__menu">
          <div className="support-dropdown__header">Help & Support</div>
          {SUPPORT_LINKS.map((link) => (
            <a
              key={link.id}
              className="support-dropdown__item"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
            >
              <span className="support-dropdown__icon">{link.icon}</span>
              <div className="support-dropdown__text">
                <span className="support-dropdown__label">{link.label}</span>
                <span className="support-dropdown__description">{link.description}</span>
              </div>
            </a>
          ))}
          <div className="support-dropdown__contacts">
            <span className="support-dropdown__contacts-label">CONTACT US</span>
            <div className="support-dropdown__contacts-list">
              {CONTACTS.map((contact) => (
                <a
                  key={contact.alias}
                  className="support-dropdown__contact-chip"
                  href={`https://my.slack.com/app_redirect?channel=@${contact.alias}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                >
                  <img className="support-dropdown__contact-avatar" src={generateProfilePictureUrl(contact.alias)} alt={contact.alias} />
                  <span className="support-dropdown__contact-alias">{contact.alias}@</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
